import { Weight, Truck, CheckCircle2 } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { fleet, trailerTypes } from "@/data/content";
import { useSiteContent } from "@/i18n/SiteContentContext";

export const Fleet = () => {
  const { get } = useSiteContent();
  const fleetList = get("fleet", fleet);
  return (
  <section id="fleet" data-testid="fleet-section" className="honeycomb-bg-light relative overflow-hidden bg-navy py-28 text-white md:py-40">
    <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-[60rem] -translate-x-1/2 rounded-full bg-sky/10 blur-3xl" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <SectionHeading
        dark
        number="03"
        eyebrow="The Showroom"
        title={<>Swedish muscle, <span className="text-gold">Gulf-ready.</span></>}
        description="A modern fleet of 25+ trucks and trailers — Volvo FH12 and FM12 prime movers rated for 80 tons, GPS-tracked in real time, paired with five specialized trailer types."
      />

      <div className="grid gap-10 lg:grid-cols-2">
        {fleetList.map((v, i) => (
          <Reveal key={v.name} delay={i * 0.12}>
            <div data-testid={`fleet-card-${i}`} className="glass-dark hex-card group overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-gold/40 hover:shadow-[0_0_60px_-10px_rgba(234,179,8,0.35)]">
              <div className="relative h-48 overflow-hidden md:h-60">
                <img src={v.image} alt={v.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
                <span className="absolute left-6 top-6 rounded-full bg-gold px-4 py-1.5 font-jakarta text-xs font-extrabold uppercase tracking-widest text-navy">
                  {v.capacity}
                </span>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3">
                  <Truck className="h-6 w-6 text-gold" />
                  <h3 className="font-jakarta text-2xl font-extrabold md:text-3xl">{v.name}</h3>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-white/60">
                  <Weight className="h-4 w-4 text-sky" />
                  Max capacity {v.capacity} · GCC certified
                </div>
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {v.specs.map((spec) => (
                    <div key={spec} className="flex items-center gap-2 text-sm text-white/70">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-gold" />
                      {spec}
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-2 border-t border-white/10 pt-6">
                  {v.trailers.map((t) => (
                    <span key={t} className="rounded-full border border-sky/25 bg-sky/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-sky">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14">
        <div className="glass-dark hex-card flex flex-col items-center gap-5 p-8 text-center md:flex-row md:justify-between md:text-left">
          <div>
            <h3 className="font-jakarta text-lg font-bold text-white">Five trailer types, one phone call</h3>
            <p className="mt-1 text-sm text-white/50">Every prime mover pairs with any trailer in the yard — matched to your cargo in minutes.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {trailerTypes.map((t) => (
              <span key={t} data-testid={`trailer-type-${t.toLowerCase().replace(" ", "-")}`} className="rounded-full border border-gold/30 bg-gold/10 px-5 py-2 font-jakarta text-xs font-bold uppercase tracking-widest text-gold">
                {t}
              </span>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
